import React, { useEffect, useState } from 'react';
import {
  AutocompleteInput,
  BulkExportButton,
  Button,
  Datagrid,
  downloadCSV,
  Error,
  List,
  ReferenceField,
  ReferenceInput,
  Show,
  Tab,
  TabbedShowLayout,
  TextField,
  TextInput,
  useGetList,
  useNotify,
  useQuery,
  useQueryWithStore,
  useRedirect,
  useRefresh,
  useUnselectAll,
  Pagination,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { gql, useMutation as apolloUseMutation } from '@apollo/react-hooks';


import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { makeStyles } from '@material-ui/core/styles';
import { Typography } from "@material-ui/core";
import MenuItem from '@material-ui/core/MenuItem';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import Tooltip from '@material-ui/core/Tooltip';
import Alert from '@material-ui/lab/Alert';
import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';

const nameLikeSearch = (n) => {
  if (n && n.length >= 2) {
    return {
      name__like: n,
    }
  }
}

const recordFilters = [
  <ReferenceInput label="Project" source="project_id" reference="Project" filterToQuery={nameLikeSearch} alwaysOn>
    <AutocompleteInput source="name"/>
  </ReferenceInput>,
  <TextInput label="Record Type name" source="record_type_name" alwaysOn/>,
  <TextInput label="Global Search" source="record_value.value__like" alwaysOn
             helperText={"Search for any description in the record matching the text exactly"}/>,
  <TextInput label="ID contains" source="record_id__like" alwaysOn/>,
];

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
  },
  paper: {
    padding: theme.spacing(2),
    margin: theme.spacing(1),
    textAlign: 'center',
    color: theme.palette.text.secondary,
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 180,
  },
  selectEmpty: {
    marginTop: theme.spacing(2),
  },
  dataLoss: {
    backgroundColor: '#fff4e5'
  },
  requiredData: {
    backgroundColor: '#ffe5e5'
  },
  heading: {
    fontSize: theme.typography.pxToRem(15),
    flexBasis: '33.33%',
    flexShrink: 0,
  },
  secondaryHeading: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
  },
  table: {
    minWidth: 650,
  },
}));


export function InlineRecordList({ records, descriptions }) {
  const classes = useStyles();
  const [expanded, setExpanded] = React.useState(false);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <div className={classes.root}>
      <Typography variant={'h5'}>Review Selected Records:</Typography>
      {records.map(r => {
        const values = {};
        r.values.forEach(v => {
          const d = descriptions.find(d => d.id === v.description.id)
          values[d.name] = v.value;
        })
        return (
          <Accordion expanded={expanded === r.id} onChange={handleChange(r.id)}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon/>}
            >
              <Typography className={classes.heading}>{r.record_id}</Typography>
              <Typography className={classes.secondaryHeading}>{values.Status && values.Status || ''}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper}>
                <Table className={classes.table} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Value</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(values).map(dName => (
                      <TableRow key={dName}>
                        <TableCell component="th" scope="row">
                          {dName}
                        </TableCell>
                        <TableCell align="right">
                          {values[dName]}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )
      })}
    </div>
  );
}

const ChangeRecordsRecordTypeDialog = ({ selectedIds, filterValues, ...rest }) => {
  const [open, setOpen] = useState(false);
  const [newRecordTypeId, setNewRecordTypeId] = useState(null);
  const [descriptionMapping, setDescriptionMapping] = useState({});
  const [listValueMap, setListValueMap] = useState({});
  const redirectTo = useRedirect();

  const [changeRecordsRecordType] = apolloUseMutation(gql`
      mutation ChangeRecordType(
          $records: [Int!]!
          $targetId: Int!
          $descriptionMapping: [ChangeRecordTypeDescriptionMapping!]!
          $listValueMapping: [ChangeRecordTypeListValueMapping!]!
      ) {
          changeRecordsRecordType(
              records: $records
              targetRecordTypeId: $targetId
              descriptionMapping: $descriptionMapping
              listValueMapping: $listValueMapping
          ) {
              id
          }
      }
  `);

  const classes = useStyles();
  const refresh = useRefresh();
  const notify = useNotify();
  const unselectAll = useUnselectAll();

  const { loaded: loadedRecords, error, data: recordData } = useQueryWithStore({
    type: 'getMany',
    resource: 'Record',
    payload: { ids: selectedIds }
  });

  const {
    loaded: loadedRecordTypes,
    error: recordTypeLoadError,
    data: recordTypes,
  } = useGetList('RecordType', {
    page: 1,
    perPage: 1000
  }, {}, { project_id: filterValues.project_id }, { enabled: !!(filterValues.project_id && filterValues.record_type_name) });

  const {
    loaded: loadedNewRTDescriptions,
    error: newRTDescriptionsError,
    data: newRTDescriptions,
  } = useQuery({
    type: 'getMany',
    resource: 'Description',
    payload: { ids: newRecordTypeId && recordTypes[newRecordTypeId].descriptions.map(d => d.id) },
  }, {
    enabled: !!newRecordTypeId
  });

  useEffect(() => {
    if (!newRTDescriptions) {
      setDescriptionMapping({});
      setListValueMap({});
      return;
    }
    // Reset
    let newDescriptionMapping = {};
    let newListValueMapping = {};
    //
    for (const oldD of descriptionData) {
      const sameName = newRTDescriptions.find(newRt => oldD.name === newRt.name);
      if (sameName) {
        newDescriptionMapping[oldD.id] = sameName.id;
        if (oldD.listValues && oldD.listValues.length > 0) {
          for (const oldLv of oldD.listValues) {
            const sameLvValue = sameName.listValues.find(lv => lv.value === oldLv.value);
            if (sameLvValue) {
              newListValueMapping[oldLv.id] = sameLvValue;
            }
          }
        }
      }
    }
    setDescriptionMapping(newDescriptionMapping);
    setListValueMap(newListValueMapping);
  }, [newRTDescriptions])


  const handleClick = () => setOpen(true);
  const handleDialogClose = () => {
    setOpen(false);
    setNewRecordTypeId(null)
    setDescriptionMapping({})
    setListValueMap({})
  }

  let descriptionIds = new Set();
  if (recordData && loadedRecords) {
    recordData.forEach(r => {
      r.values.forEach(v => {
        descriptionIds.add(v.description.id)
      })
    })
  }

  const { loaded: loadedDescriptions, error: errorDescriptions, data: descriptionData } = useQueryWithStore({
    type: 'getMany',
    resource: 'Description',
    payload: { ids: Array.from(descriptionIds) }
  });

  if (!filterValues.project_id || !filterValues.record_type_name) {
    return (
      <Tooltip title="Must filter by both project and record type name first">
        <span>
          <Button label="Change RT" disabled/>
        </span>
      </Tooltip>
    );
  }

  if (!loadedDescriptions || !loadedRecords) {
    return <Button label="Loading"/>;
  }
  if (error || errorDescriptions) {
    return <Error error={error || errorDescriptions}/>;
  }

  const isLvMappingRequired = (descriptionId, lv) => {
    if (typeof descriptionId !== "number") {
      throw new Error('Description id not passed')
    }
    if (!lv) {
      throw new Error('List Value not passed')
    }

    for (const record of recordData) {
      const value = record.values.find(v => v.description.id === descriptionId);
      if (!value) {
        continue;
      }
      if (value.value === lv.value) {
        return true
      }
    }
  }

  const handleMigrate = async () => {
    let formattedDescriptionMapping = Object.keys(descriptionMapping).map(id => ({
      source_description_id: parseInt(id),
      target_description_id: descriptionMapping[id]
    })).filter(m => !!(m.source_description_id && m.target_description_id));

    let formattedLVMap = Object.keys(listValueMap).map(id => ({
      source_list_value_id: parseInt(id),
      target_list_value_id: listValueMap[id].id
    })).filter(v => !!(v.source_list_value_id && v.target_list_value_id));

    const res = await changeRecordsRecordType({
      variables: {
        records: selectedIds,
        targetId: newRecordTypeId,
        descriptionMapping: formattedDescriptionMapping,
        listValueMapping: formattedLVMap
      }
    });

    if (res.data.errors?.length >= 1) {
      throw new Error(res.errors[0].message)
    }

    notify('Saved');
    redirectTo(`/Record`);
    unselectAll('Record');
    refresh();
  }

  const mappedTargetDescriptions = new Set(Object.keys(descriptionMapping).map(k => descriptionMapping[k]));

  return (
    <>
      <Button label="Change RT" onClick={handleClick}/>
      <Dialog
        open={open}
        onClose={handleDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Move these records to a new record type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Move the selected records to a new record type. To do this we need to map the previous record type
            descriptions with the new record type's descriptions. If the two record types have descriptions that match
            each other perfectly, no extra work is required, however usually two record types are setup in different way
            and we have to map the previous record type's data to the target record types data.
            Any of the descriptions that do not have a target description will appear with a yellow background, and
            dropdown/list value descriptions requires you to match each used dropdown option.
            <Alert severity="warning">Any descriptions that do not have a matching new description will have its data
              LOST forever</Alert>
          </DialogContentText>
          <InlineRecordList records={recordData} descriptions={descriptionData}/>
          <Paper className={classes.paper} variant={"outlined"}>
            <Typography variant="h4" align={"center"} color="textSecondary" gutterBottom>Target Record Type</Typography>
            <FormControl className={classes.formControl}>
              <Select
                id={'target-rt-selector'}
                variant={"outlined"}
                value={newRecordTypeId}
                onChange={(evt) => {
                  setNewRecordTypeId(evt.target.value)
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {loadedRecordTypes && recordTypes && Object.keys(recordTypes).map(rtId => (
                  <MenuItem value={recordTypes[rtId].id}>{recordTypes[rtId].name}</MenuItem>))}
              </Select>
            </FormControl>
          </Paper>
          {newRTDescriptions &&
          <>
            <Typography variant="h4" color="textSecondary" align={"center"}>Description Mapping</Typography>
            <Paper className={`${classes.paper}`}
                   variant="outlined">
              <Grid container spacing={1} justify={"center"}>
                <Grid item xs={6}>
                  From Description
                </Grid>
                <Grid item xs={6}>
                  To Target Description
                </Grid>
              </Grid>
            </Paper>
          </>}
          {newRTDescriptions && descriptionData.map(d => (
            <Paper
              className={`${classes.paper} ${!descriptionMapping[d.id] && (d.name === 'ID' ? classes.requiredData : classes.dataLoss) || ''}`}
              variant="outlined">
              <Grid container spacing={1} justify={"center"}>
                <Grid item xs={6}>
                  {d.name}
                </Grid>
                <Grid item xs={6}>
                  <FormControl className={classes.formControl}>
                    <Select
                      value={descriptionMapping[d.id] || ''}
                      label="new Description"
                      onChange={(evt) => {
                        setDescriptionMapping({
                          ...descriptionMapping,
                          [d.id]: evt.target.value
                        })
                      }}
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {newRTDescriptions && newRTDescriptions.filter(nD => descriptionMapping[d.id] === nD.id || !mappedTargetDescriptions.has(nD.id)).map(d => (
                        <MenuItem value={d.id}>{d.name}</MenuItem>))}
                    </Select>
                  </FormControl>
                </Grid>


                {descriptionMapping[d.id] && d.listValues && d.listValues.filter((lv) => isLvMappingRequired(d.id, lv)).map(lv => (
                  <Grid item xs={10} rowSpacing={1}>
                    <Paper variant="outlined" square
                           className={`${!listValueMap[lv.id] && classes.requiredData || ''}`}>
                      <Grid container spacing={0}>
                        <Grid item xs={6}>
                          {lv.value}
                        </Grid>
                        <Grid item xs={6}>
                          <FormControl className={classes.formControl}>
                            <Select
                              value={listValueMap[lv.id] && listValueMap[lv.id].id || ''}
                              label="new value"
                              onChange={(evt) => {
                                const mappedD = newRTDescriptions.find(newD => newD.id === descriptionMapping[d.id]);
                                const mappedLV = mappedD.listValues.find(lv => lv.id === evt.target.value);
                                setListValueMap({
                                  ...listValueMap,
                                  [lv.id]: mappedLV
                                })
                              }}
                            >
                              <MenuItem value="">
                                <em>None</em>
                              </MenuItem>
                              {newRTDescriptions && newRTDescriptions.find(newD => newD.id === descriptionMapping[d.id])?.listValues.map(newLv => (
                                <MenuItem value={newLv.id}>{newLv.value}</MenuItem>))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          ))}
          {newRTDescriptions && <>
            <Paper className={classes.paper} elevation={6}>
              <Typography variant="h4" align={"center"} color="textSecondary" gutterBottom>Summary</Typography>
              <Paper className={classes.paper} variant={'outlined'}>
                <Typography variant="h6" align={"left"} color="textSecondary" gutterBottom># Of descriptions with no
                  target (will be
                  deleted): {Array.from(descriptionIds).length - Object.keys(descriptionMapping).length}</Typography>
              </Paper>
              <Paper className={classes.paper} variant={'outlined'}>
                <Typography variant="h6" align={"left"} color="textSecondary"># Of descriptions in the target
                  that is not mapped
                  to: {newRTDescriptions.filter(newD => !mappedTargetDescriptions.has(newD.id)).length}</Typography>
                {newRTDescriptions.filter(newD => !mappedTargetDescriptions.has(newD.id)).map(d => <Typography
                  color={'textSecondary'}>{d.name}</Typography>)}
              </Paper>

              Highly recommend exporting the data for backup:
              <BulkExportButton/>
              <Button onClick={handleMigrate} label={'MIGRATE'}/>
            </Paper>
          </>}
        </DialogContent>
      </Dialog>
    </>
  );
}

const PostBulkActionButtons = props => (
  <>
    <ChangeRecordsRecordTypeDialog label="Reset Views" {...props} />
    <BulkExportButton/>
  </>
);

const recordExporter = async (records, fetchRelatedRecords, dataloader) => {
  // will call dataProvider.getMany('posts', { ids: records.map(record => record.post_id) }), ignoring duplicate and empty post_id
  const recordTypes = await fetchRelatedRecords(records, 'record_type.id', 'RecordType')
  const projects = await fetchRelatedRecords(records, 'project.id', 'Project')
  const descriptionIds = new Set();
  records.forEach(r => r.values.forEach(v => descriptionIds.add(v.description.id)))
  const { data: descriptions } = await dataloader.getMany('Description', { ids: Array.from(descriptionIds) })
  const data = records.map(r => {
    let rData = {
      id: r.id,
      recordID: r.record_id,
      recordTypeName: recordTypes[r.record_type.id].name,
      project: projects[r.project.id].name,
    }
    r.values.forEach(v => rData[descriptions.find(d => d.id === v.description.id).name] = v.value)
    return rData;
  });

  jsonExport(data, {
    headers: ['id', 'recordID', 'recordTypeName', 'project', ...descriptions.map(d => d.name)],
  }, (err, csv) => {
    downloadCSV(csv, 'recordMigrationExport');
  });
}

const RecordPagination = props => <Pagination rowsPerPageOptions={[10, 25, 50, 100, 250, 500, 1000]} {...props} />;

export const RecordListView = (props) => (
  <List {...props}
        filters={recordFilters}
        bulkActionButtons={<PostBulkActionButtons/>}
        exporter={recordExporter}
        pagination={<RecordPagination />}
        >
    <Datagrid rowClick="show">
      <TextField source="id"/>
      <TextField source="record_id" label="ID"/>
      <ReferenceField reference="Project" source="project.id" label="Project" link="show">
        <TextField source="name"/>
      </ReferenceField>
      <ReferenceField label={"Record Type"} reference="RecordType" source="record_type.id" link={false}>
        <TextField source="name"/>
      </ReferenceField>
    </Datagrid>
  </List>
);


export const RecordView = (props) => (
  <Show {...props}>
    <TabbedShowLayout>
      <Tab label="Summary">
        <TextField source="id"/>
        <TextField source="record_id" label="ID"/>
        <ReferenceField reference="Project" source="project.id" label="Project" link="show">
          <TextField source="name"/>
        </ReferenceField>
        <ReferenceField label='Record Type' reference="RecordType" source="record_type.id" link={false}>
          <TextField source="name"/>
        </ReferenceField>
      </Tab>
    </TabbedShowLayout>
  </Show>
);
